import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import Drawer from "../../../Component/Drawer";
import { postRequest } from "../../../utils/api";

interface PlanDrawerProps {
  show: boolean;
  planData?: any;
  handleClose: () => void;
  refreshPlans: () => void;
  onMakeDraft?: (planId: string) => void;
}

// --- Yup Validation Schema ---
const planSchema = yup.object().shape({
  title: yup.string().trim().required("Plan Name is required"),
  planType: yup.string().required(),
  monthlyPrice: yup.string().when("planType", {
    is: (val: string) => val === "recurring" || val === "lifetime",
    then: (schema) => schema.required("Price is required").test("is-valid-num", "Must be a valid number ≥ 0", (value) => value !== undefined && value !== "" && !isNaN(Number(value)) && Number(value) >= 0),
    otherwise: (schema) => schema.notRequired(),
  }),
  yearlyPrice: yup.string().when("planType", {
    is: "recurring",
    then: (schema) => schema.required("Yearly price is required").test("is-valid-num", "Must be a valid number ≥ 0", (value) => value !== undefined && value !== "" && !isNaN(Number(value)) && Number(value) >= 0),
    otherwise: (schema) => schema.notRequired(),
  }),
});

// Right-side drawer to create / edit a plan
const PlanDrawer: React.FC<PlanDrawerProps> = ({ show, planData, handleClose, refreshPlans, onMakeDraft }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [planType, setPlanType] = useState("recurring"); // 'recurring' | 'lifetime' | 'free'
  const [monthlyPrice, setMonthlyPrice] = useState("");
  const [yearlyPrice, setYearlyPrice] = useState("");
  const [quarterlyPrice, setQuarterlyPrice] = useState("");
  const [visibility, setVisibility] = useState<"hidden" | "credit_line" | "own_card" | "both">("both");
  const [badge, setBadge] = useState("");
  const [isBlackCard, setIsBlackCard] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Pre-fill form when planData changes (Edit Mode)
  useEffect(() => {
    if (planData) {
      setTitle(planData.ppm_plan_title || "");
      setDescription(planData.ppm_plan_description || "");
      setVisibility(planData.ppm_plan_visibility || "both");
      setBadge(planData.ppm_plan_badge || "");
      setIsBlackCard(planData.ppm_plan_is_black_card || false);

      const mPrice = planData.ppm_plan_monthly_price || 0;
      const yPrice = planData.ppm_plan_yearly_price || 0;

      // Deduce plan type from prices
      if (mPrice === 0 && yPrice === 0) {
        setPlanType("free");
        setMonthlyPrice("");
        setYearlyPrice("");
      } else if (yPrice === 0 && mPrice > 0) {
        setPlanType("lifetime");
        setMonthlyPrice(String(mPrice));
        setYearlyPrice("");
      } else {
        setPlanType("recurring");
        setMonthlyPrice(String(mPrice));
        setYearlyPrice(String(yPrice));
      }
      setQuarterlyPrice(planData.ppm_plan_quarterly_price ? String(planData.ppm_plan_quarterly_price) : "");
    } else {
      setTitle("");
      setDescription("");
      setPlanType("recurring");
      setMonthlyPrice("");
      setYearlyPrice("");
      setQuarterlyPrice("");
      setVisibility("both");
      setBadge("");
      setIsBlackCard(false);
    }
    setErrors({});
  }, [planData, show]);

  const onHide = () => {
    setErrors({});
    handleClose();
  };

  const handleSave = async () => {
    try {
      await planSchema.validate({ title, planType, monthlyPrice, yearlyPrice }, { abortEarly: false });
      setErrors({});
    } catch (err: any) {
      const validationErrors: { [key: string]: string } = {};
      if (err.inner) {
        err.inner.forEach((error: any) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
      }
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      let finalMonthly = 0;
      let finalYearly = 0;
      let finalQuarterly = 0;
      if (planType === "recurring") {
        finalMonthly = Number(monthlyPrice);
        finalYearly = Number(yearlyPrice);
        finalQuarterly = Number(quarterlyPrice) || 0;
      } else if (planType === "lifetime") {
        finalMonthly = Number(monthlyPrice); // Storing the one-time fee in the monthly column
      }

      const payload = {
        ppm_plan_title: title,
        ppm_plan_description: description,
        ppm_plan_type: planType,
        ppm_plan_monthly_price: finalMonthly,
        ppm_plan_yearly_price: finalYearly,
        ppm_plan_quarterly_price: finalQuarterly,
        ppm_plan_visibility: visibility,
        ppm_plan_badge: badge,
        ppm_plan_is_black_card: isBlackCard,
      };

      const apiRoute = planData && planData._id ? `/plans/update/${planData._id}` : `/plans/add`;
      const response: any = await postRequest(apiRoute, payload);

      if (response.status === "success") {
        toast.success(response.message || `Plan ${planData ? "updated" : "created"} successfully!`);
        refreshPlans();
        onHide();
      } else {
        toast.error(response.message || `Failed to ${planData ? "update" : "create"} plan`);
      }
    } catch (error: any) {
      toast.error(error.message || "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlanTypeChange = (newType: string) => {
    setPlanType(newType);
    setErrors({ ...errors, monthlyPrice: "", yearlyPrice: "" });
  };

  const m = Number(monthlyPrice) || 0;
  const y = Number(yearlyPrice) || 0;
  const yearSave = m > 0 && y > 0 ? Math.round((1 - y / (m * 12)) * 100) : 0;
  const subs = planData?.ppm_total_subscriptions || 0;

  const priceInput = (label: string, value: string, set: (v: string) => void, key: string, help?: string) => (
    <div>
      <label className="cx-label">{label} <span className="down">*</span></label>
      <input
        type="number"
        min="0"
        placeholder="0"
        className={`cx-input mono ${errors[key] ? "bad" : ""}`}
        value={value}
        onChange={(e) => { set(e.target.value); if (errors[key]) setErrors({ ...errors, [key]: "" }); }}
      />
      {errors[key] ? <p className="cx-err">{errors[key]}</p> : help && <p className="cx-help">{help}</p>}
    </div>
  );

  return (
    <Drawer
      show={show}
      onClose={onHide}
      title={planData ? `Edit plan · ${planData.ppm_plan_title}` : "New plan"}
      subtitle="Applies at each tenant's next billing cycle."
      footer={<>
        {planData && planData.ppm_plan_visibility !== "hidden" && onMakeDraft && (
          <>
            <button type="button" className="cx-btn cx-btn-danger" disabled={isSubmitting} onClick={() => { onHide(); onMakeDraft(planData._id); }}>
              <i className="ri-archive-line"></i>Hide plan
            </button>
            <div className="sp"></div>
          </>
        )}
        <button type="button" className="cx-btn cx-btn-light" onClick={onHide} disabled={isSubmitting}>Cancel</button>
        <button type="button" className="cx-btn cx-btn-primary" onClick={handleSave} disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Save plan"}
        </button>
      </>}
    >
      {planData && subs > 0 && (
        <div className="cx-banner">
          <i className="ri-information-line"></i>
          <div><b>{subs.toLocaleString("en-IN")} active subscribers</b> are on this plan. Price changes apply from each tenant's next billing cycle.</div>
        </div>
      )}

      <div className="cx-fgrid">
        <div>
          <label className="cx-label">Plan title <span className="down">*</span></label>
          <input
            className={`cx-input ${errors.title ? "bad" : ""}`}
            placeholder="e.g., Pro Starter"
            value={title}
            onChange={(e) => { setTitle(e.target.value); if (errors.title) setErrors({ ...errors, title: "" }); }}
          />
          {errors.title && <p className="cx-err">{errors.title}</p>}
        </div>
        <div>
          <label className="cx-label">Plan type <span className="down">*</span></label>
          <select className="cx-select" value={planType} onChange={(e) => handlePlanTypeChange(e.target.value)}>
            <option value="recurring">Recurring (monthly / yearly)</option>
            <option value="lifetime">Lifetime (one-time)</option>
            <option value="free">Free forever</option>
          </select>
        </div>
        <div className="full">
          <label className="cx-label">Description</label>
          <input
            className="cx-input"
            placeholder="Short outline of the plan's ideal audience"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {planType === "recurring" && <>
          {priceInput("Monthly price (₹)", monthlyPrice, setMonthlyPrice, "monthlyPrice")}
          {priceInput("Yearly price (₹)", yearlyPrice, setYearlyPrice, "yearlyPrice", yearSave > 0 ? `Saves ${yearSave}% vs paying monthly` : "Discount is calculated automatically")}
          <div>
            <label className="cx-label">Quarterly price (₹)</label>
            <input
              type="number"
              min="0"
              placeholder="Leave blank — no quarterly option"
              className="cx-input mono"
              value={quarterlyPrice}
              onChange={(e) => setQuarterlyPrice(e.target.value)}
            />
            <p className="cx-help">Offered as a billing cycle on every portal. Leave blank and quarterly billing still works everywhere, just at 3× monthly price with no discount.</p>
          </div>
        </>}
        {planType === "lifetime" && <div className="full">{priceInput("One-time price (₹)", monthlyPrice, setMonthlyPrice, "monthlyPrice")}</div>}
        {planType === "free" && <>
          <div><label className="cx-label">Monthly price (₹)</label><input className="cx-input mono" value="0" disabled /></div>
          <div><label className="cx-label">Yearly price (₹)</label><input className="cx-input mono" value="0" disabled /></div>
        </>}

        <div>
          <label className="cx-label">Badge</label>
          <select className="cx-select" value={badge} onChange={(e) => setBadge(e.target.value)}>
            <option value="">None</option>
            <option value="Most Popular">Most Popular</option>
            <option value="Best Value">Best Value</option>
            <option value="Recommended">Recommended</option>
          </select>
        </div>
        <div className="full cx-togrow">
          <label className="cx-switch">
            <input type="checkbox" checked={isBlackCard} onChange={(e) => setIsBlackCard(e.target.checked)} />
            <span></span>
          </label>
          <span className="fs12">Black card style (premium dark card on the pricing grid)</span>
        </div>
        <div className="full">
          <label className="cx-label">Visibility</label>
          <select className="cx-select" value={visibility} onChange={(e) => setVisibility(e.target.value as typeof visibility)}>
            <option value="both">Both portals — shown everywhere</option>
            <option value="credit_line">Credit-line only — wa.uomox.com / crm.uomox.com</option>
            <option value="own_card">Own-card only — whatsapp.uomox.com (app.uomox.com)</option>
            <option value="hidden">Hidden — not shown anywhere</option>
          </select>
        </div>
      </div>
    </Drawer>
  );
};

export default PlanDrawer;
